import { type FunctionComponent } from 'react';

import { Image } from '@/core/Image/Image';
import { isB2B } from '@/core/settings/utils';
import { BannerCampaign } from '@/productDiscovery/CommercialAnimation/interfaces/bannerCampaign';
import fetchedImgSrc from '@/sellerAdsManagement/PartnerAdBannerCampaign/assets/b2b-background.png';
import { PartnerAdBannerLabel } from '@/sellerAdsManagement/PartnerAdBannerCampaign/components/PartnerAdBannerLabel/PartnerAdBannerLabel';
import { defaultThemeColors } from '@/sellerAdsManagement/PartnerAdBannerCampaign/interfaces/themeColors';
import { usePartnerAdTheme } from '@/sellerAdsManagement/PartnerAdTheme/hooks/usePartnerAdTheme';

export interface RectangularBlockProps {
  banner: BannerCampaign;
}

const SIZES = {
  small: {
    ldpi: {
      width: 228,
      height: 156,
    },
    hdpi: {
      width: 456,
      height: 312,
    },
  },
  medium: {
    ldpi: {
      width: 228,
      height: 156,
    },
    hdpi: {
      width: 456,
      height: 312,
    },
  },
  large: {
    ldpi: {
      width: 228,
      height: 156,
    },
    hdpi: {
      width: 456,
      height: 312,
    },
  },
};

export const RectangularBlock: FunctionComponent<RectangularBlockProps> = ({
  banner,
}) => {
  const theme = usePartnerAdTheme();

  const themeColors = {
    '--banner-main-color':
      theme && theme.colors ? theme.colors.main : defaultThemeColors.main,
    '--banner-accent-color':
      theme && theme.colors ? theme.colors.accent : defaultThemeColors.accent,
    '--banner-labor_text-color':
      theme && theme.colors
        ? theme.colors.labor_text
        : defaultThemeColors.labor_text,
    '--banner-secondary-color':
      theme && theme.colors
        ? theme.colors.secondary
        : defaultThemeColors.secondary,
  };

  const b2bStyle = isB2B() ? { backgroundImage: `url(${fetchedImgSrc})` } : '';

  return (
    <div
      className="relative flex h-full w-full bg-[--banner-main-color] p-m
        bg-blend-multiply max-desktop:gap-s"
      style={{ ...themeColors, ...b2bStyle }}
    >
      {banner.label && (
        <PartnerAdBannerLabel
          label={banner.label}
          subtitleClassName="mt-m text-[--banner-labor_text-color] overflow-hidden text-ellipsis whitespace-nowrap text-[11px] min-[948px]:text-[calc(0.210684px+100vw*0.012492)] min-[1424px]:text-[17px]"
          lineClassName="font-bold overflow-hidden text-ellipsis whitespace-nowrap"
          middleLineClassName="text-[17px]
              min-[948px]:text-[calc(-2.8px+100vw*0.018738)]
              min-[1424px]:text-[26px]"
          smallLineClassName="text-[11px]
              min-[948px]:text-[calc(0.187274px+100vw*0.011103)]
              min-[1424px]:text-[15px]"
          bigLineClassName="text-[27px]
              min-[948px]:text-[calc(0.515005px+100vw*0.030536)]
              min-[1424px]:text-[43px]"
          labelClassName="flex flex-col text-[--banner-accent-color] p-s w-[52%]"
          subtitle={banner.subtitle}
        />
      )}
      <div className="m-auto h-full w-[48%]">
        {banner.logoUrl && (
          <div
            className="mt-auto flex h-[25%] w-full justify-center rounded-card
              bg-foundation-inverted p-xs min-[948px]:p-s"
          >
            <Image
              className="h-auto max-h-full w-auto max-w-full object-contain"
              src={banner.logoUrl}
              alt="brand-logo"
              height={undefined}
              width={undefined}
            />
          </div>
        )}
        <Image
          width={SIZES.small.ldpi.width}
          height={SIZES.small.ldpi.height}
          loading="eager"
          alt={banner.image.altText}
          src={`${banner.image.mobileUrl}?w=${SIZES.small.ldpi.width}&h=${SIZES.small.ldpi.height}&fit=cover`}
          srcSet={`${banner.image.mobileUrl}?w=${SIZES.small.ldpi.width}&h=${SIZES.small.ldpi.height}&fit=cover 1x, ${banner.image.mobileUrl}?w=${SIZES.small.hdpi.width}&h=${SIZES.small.hdpi.height}&fit=cover 2x`}
          className="mt-s h-auto w-full rounded-container
            bg-[--banner-secondary-color] object-cover"
        >
          <source
            srcSet={`${banner.image.desktopUrl}?w=${SIZES.large.ldpi.width}&h=${SIZES.large.ldpi.height}&fit=cover&format=webp 1x, ${banner.image.desktopUrl}?w=${SIZES.large.hdpi.width}&h=${SIZES.large.hdpi.height}&fit=cover&format=webp 2x`}
            media="(min-width: 1024px)"
            type="image/webp"
          />
          <source
            srcSet={`${banner.image.desktopUrl}?w=${SIZES.large.ldpi.width}&h=${SIZES.large.ldpi.height}&fit=cover 1x, ${banner.image.desktopUrl}?w=${SIZES.large.hdpi.width}&h=${SIZES.large.hdpi.height}&fit=cover 2x`}
            media="(min-width: 1024px)"
          />
          <source
            srcSet={`${banner.image.desktopUrl}?w=${SIZES.medium.ldpi.width}&h=${SIZES.medium.ldpi.height}&fit=cover&format=webp 1x, ${banner.image.desktopUrl}?w=${SIZES.medium.hdpi.width}&h=${SIZES.medium.hdpi.height}&fit=cover&format=webp 2x`}
            media="(min-width: 536px)"
            type="image/webp"
          />
          <source
            srcSet={`${banner.image.desktopUrl}?w=${SIZES.medium.ldpi.width}&h=${SIZES.medium.ldpi.height}&fit=cover 1x, ${banner.image.desktopUrl}?w=${SIZES.medium.hdpi.width}&h=${SIZES.medium.hdpi.height}&fit=cover 2x`}
            media="(min-width: 536px)"
          />
          <source
            srcSet={`${banner.image.mobileUrl}?w=${SIZES.small.ldpi.width}&h=${SIZES.small.ldpi.height}&fit=cover&format=webp 1x, ${banner.image.mobileUrl}?w=${SIZES.small.hdpi.width}&h=${SIZES.small.hdpi.height}&fit=cover&format=webp 2x`}
            type="image/webp"
          />
        </Image>
      </div>
    </div>
  );
};

RectangularBlock.displayName = 'RectangularBlock';
